import {ConsentCategory, ConsentCategoryName} from './consent';

/**
 * Expires the first-party cookies a withdrawn consent category left behind.
 *
 * Termly blocks scripts from loading; with autoBlock off it never sweeps what already ran, and
 * nothing else does either. Without this the Google Analytics client id and the Hotjar user id
 * outlive the withdrawal, so a visitor who re-consents later is recognised as the same person
 * they asked us to stop recognising — which is the thing withdrawing consent is meant to stop.
 *
 * Only cookies on our own domain are reachable from here. Third-party ones (doubleclick,
 * facebook, bing) can be cleared by the vendor or the browser, not by us.
 *
 * Kept in sync with src/shared/helpers/clearTrackingCookies.js in amplify-website-v2-frontend.
 */

// Prefixes rather than names: GA suffixes its session cookie with the container id
// (_ga_DX6DTG5SFS) and Hotjar with the site id (_hjSessionUser_1847658), so neither can be
// listed literally.
const TRACKING_COOKIE_PREFIXES: Partial<Record<ConsentCategoryName, string[]>> = {
    [ConsentCategory.analytics]: ['_ga', '_gid', '_gat', '_clck', '_clsk', '_hj'],
    [ConsentCategory.advertising]: ['_fbp', '_gcl_au', '_uetsid', '_uetvid'],
};

const expire = (name: string, domain?: string): void => {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domain ? `; domain=${domain}` : ''}`;
};

/**
 * Every domain the cookie could have been scoped to, from the exact host up to the registrable
 * one. A cookie's own domain is not readable from document.cookie, and a delete only takes
 * effect when the domain matches what set it, so each candidate is tried; the misses are inert.
 */
const candidateDomains = (): Array<string | undefined> => {
    const parts = window.location.hostname.split('.');
    const domains: Array<string | undefined> = [undefined];

    for (let i = 0; i < parts.length - 1; i++) {
        domains.push(`.${parts.slice(i).join('.')}`);
    }

    return domains;
};

/** @param categories the categories the visitor has just withdrawn. */
export const clearTrackingCookies = (categories: ConsentCategoryName[]): void => {
    const prefixes = categories.flatMap((category) => TRACKING_COOKIE_PREFIXES[category] || []);
    if (!prefixes.length) return;

    const domains = candidateDomains();

    document.cookie.split('; ').forEach((pair) => {
        const name = pair.split('=')[0];
        if (!name || !prefixes.some((prefix) => name.startsWith(prefix))) return;

        domains.forEach((domain) => expire(name, domain));
    });
};

export default clearTrackingCookies;
